import api from "@shared/api/client";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { AddressesContextValue, AddressesState } from "./types";
import { useAddressCrud } from "./useAddressCrud";
import { useProviderAssignment } from "./useProviderAssignment";

const AddressesContext = createContext<AddressesContextValue | null>(null);

const AddressesProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation(["common", "addresses", "status"]);
  const [state, setState] = useState<AddressesState>({
    addresses: [],
    allProviders: [],
    loading: true,
    expandedId: null,
    dialogState: { mode: "closed", editing: null, name: "", error: "" },
    assignState: {
      open: false,
      addressId: null,
      providerId: "",
      username: "",
      password: "",
      accountKeyword: "",
      skipPayment: false,
      taxDeduction: false,
      taxDeductionAmount: "",
      meterReadingEnabled: false,
      invoiceEmailTo: "",
    },
    editProviderState: {
      open: false,
      addressId: null,
      providerId: null,
      username: "",
      password: "",
      accountKeyword: "",
      skipPayment: false,
      taxDeduction: false,
      taxDeductionAmount: "",
      hasPassword: false,
      meterReadingEnabled: false,
      invoiceEmailTo: "",
    },
  });

  const fetchData = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const [addrs, provs] = await Promise.all([api.addresses.list(), api.providers.list()]);
      setState((prev) => ({ ...prev, addresses: addrs, allProviders: provs }));
    } catch {
      setState((prev) => ({ ...prev, addresses: [] }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const contextSetState = useCallback((partial: Partial<AddressesState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const setExpandedId = useCallback(
    (id: number | null) => {
      contextSetState({ expandedId: id });
    },
    [contextSetState],
  );

  const crud = useAddressCrud(state.dialogState, contextSetState, fetchData, t);
  const assignment = useProviderAssignment(state.assignState, state.editProviderState, contextSetState, fetchData, t);

  return (
    <AddressesContext.Provider
      value={{
        ...state,
        fetchData,
        setState: contextSetState,
        setExpandedId,
        openCreate: crud.openCreate,
        openEdit: crud.openEdit,
        handleSave: crud.handleSave,
        handleDelete: crud.handleDelete,
        openAssign: assignment.openAssign,
        handleAssign: assignment.handleAssign,
        handleRemoveProvider: assignment.handleRemoveProvider,
        openEditProvider: assignment.openEditProvider,
        handleEditSave: assignment.handleEditSave,
        t,
      }}
    >
      {children}
    </AddressesContext.Provider>
  );
};

const useAddresses = () => {
  const ctx = useContext(AddressesContext);
  if (!ctx) throw new Error("useAddresses must be used within AddressesProvider");
  return ctx;
};

export { AddressesProvider, useAddresses };
